"use client"

import { useState, useEffect } from "react"
import {
  Tag,
  Grid,
  DollarSign,
  Clock,
  MapPin,
  AlignLeft,
  Home,
  CheckCircle,
  X,
  Search,
  User,
  Mail,
  Phone,
} from "lucide-react"
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet"
import L from "leaflet"
import logo from "../../assets/logo.png"
import "./Encuesta.css"
import { Locate } from "lucide-react"
import encuestaService from "../../services/encuestaService"

// Icono de marcador Leaflet por defecto
const markerIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

// Reposiciona la vista si cambia center
function MapUpdater({ center, zoom }) {
  const map = useMap()
  useEffect(() => {
    if (center) map.setView(center, zoom || map.getZoom())
  }, [center, zoom, map])
  return null
}

// Captura click y devuelve coords al padre
function LocationPicker({ onSelect }) {
  useMapEvents({
    click(e) {
      onSelect([e.latlng.lat, e.latlng.lng])
    },
  })
  return null
}

// Componente para centrar el mapa en una ubicación
function MapCenterController({ center, zoom }) {
  const map = useMap()

  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom || 15)
    }
  }, [center, zoom, map])

  return null
}

// Componente Modal estilo moderno
function Modal({ isOpen, onClose, type, title, message }) {
  if (!isOpen) return null

  return (
    <div className="modal-overlay-modern" onClick={type !== "loading" ? onClose : undefined}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Icono grande circular */}
        <div className={`modal-icon-large ${type}`}>
          {type === "success" && <CheckCircle size={32} />}
          {type === "error" && <X size={32} />}
          {type === "loading" && <div className="modal-spinner-large"></div>}
        </div>

        {/* Contenido del modal */}
        <div className="modal-content-modern">
          <h2 className="modal-title-large">{title}</h2>
          <p className="modal-message">{message}</p>
        </div>

        {/* Botón de acción */}
        {type !== "loading" && (
          <button className={`btn-modal-modern ${type}`} onClick={onClose}>
            {type === "success" ? "¡Perfecto!" : "Entendido"}
          </button>
        )}
      </div>
    </div>
  )
}

// Componente para pasos del formulario
function FormStep({ children, isActive }) {
  if (!isActive) return null
  return <div className="form-step">{children}</div>
}

export default function Encuesta() {
  const [form, setForm] = useState({
    // Datos del parqueadero
    nombre_parking: "",
    plazas: "",
    ubicacion: "",
    preciotarifa: "",
    horaApertura: "",
    horaCierre: "",
    descripcion: "",
    latitud: null,
    longitud: null,

    // Datos de contacto del propietario
    nombre_contacto: "",
    email_contacto: "",
    telefono_contacto: "",

    // Estado de la encuesta (no visible para el usuario)
    estado_encuesta: 0,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [modal, setModal] = useState({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  })

  const [validationErrors, setValidationErrors] = useState({
    email_contacto: "",
    telefono_contacto: "",
  })

  const [searchQuery, setSearchQuery] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 3

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))

    // Validar campos específicos en tiempo real
    if (name === "email_contacto" || name === "telefono_contacto") {
      validateField(name, value)
    }
  }

  const handleMapSelect = ([lat, lng]) => {
    setForm((prev) => ({ ...prev, latitud: lat, longitud: lng }))
  }

  const showModal = (type, title, message) => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
    })
  }

  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }))
  }

  // Validar formato de email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Validar teléfono (10 dígitos numéricos)
  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10}$/
    return phoneRegex.test(phone)
  }

  // Validar campo específico
  const validateField = (name, value) => {
    let error = ""

    if (name === "email_contacto") {
      if (value && !validateEmail(value)) {
        error = "Ingresa un correo electrónico válido (ejemplo@correo.com)"
      }
    } else if (name === "telefono_contacto") {
      if (value && !validatePhone(value)) {
        error = "El teléfono debe tener exactamente 10 dígitos numéricos"
      }
    }

    setValidationErrors((prev) => ({
      ...prev,
      [name]: error,
    }))

    return error === ""
  }

  const resetForm = () => {
    setForm({
      nombre_parking: "",
      plazas: "",
      ubicacion: "",
      preciotarifa: "",
      horaApertura: "",
      horaCierre: "",
      descripcion: "",
      latitud: null,
      longitud: null,
      nombre_contacto: "",
      email_contacto: "",
      telefono_contacto: "",
      estado_encuesta: 0,
    })
    setValidationErrors({
      email_contacto: "",
      telefono_contacto: "",
    })
    setCurrentStep(1)
  }

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
      window.scrollTo(0, 0)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      window.scrollTo(0, 0)
    }
  }

  const defaultCenter = [-0.1807, -78.4678] // Quito

  // Obtener ubicación actual del usuario
  const getCurrentLocation = () => {
    setIsGettingLocation(true)

    if (!navigator.geolocation) {
      showModal("error", "Geolocalización no disponible", "Tu navegador no soporta geolocalización.")
      setIsGettingLocation(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setForm((prev) => ({ ...prev, latitud: latitude, longitud: longitude }))
        setIsGettingLocation(false)
        showModal("success", "¡Ubicación Obtenida!", "Se ha establecido tu ubicación actual correctamente.")
      },
      (error) => {
        let errorMessage = "No se pudo obtener tu ubicación."

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Permiso de ubicación denegado. Por favor, permite el acceso a tu ubicación."
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Información de ubicación no disponible."
            break
          case error.TIMEOUT:
            errorMessage = "Tiempo de espera agotado para obtener la ubicación."
            break
        }

        showModal("error", "Error de Ubicación", errorMessage)
        setIsGettingLocation(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    )
  }

  // Buscar ubicaciones usando Nominatim (OpenStreetMap)
  const searchLocation = async (query) => {
    if (!query.trim()) return

    setIsSearching(true)
    setShowSearchResults(false)

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=ec&addressdetails=1`,
      )

      if (!response.ok) throw new Error("Error en la búsqueda")

      const results = await response.json()
      setSearchResults(results)
      setShowSearchResults(results.length > 0)

      if (results.length === 0) {
        showModal("error", "Sin Resultados", "No se encontraron ubicaciones para tu búsqueda.")
      }
    } catch (error) {
      showModal("error", "Error de Búsqueda", "No se pudo realizar la búsqueda. Inténtalo de nuevo.")
    } finally {
      setIsSearching(false)
    }
  }

  // Seleccionar resultado de búsqueda
  const selectSearchResult = (result) => {
    const lat = Number.parseFloat(result.lat)
    const lon = Number.parseFloat(result.lon)

    setForm((prev) => ({
      ...prev,
      latitud: lat,
      longitud: lon,
      ubicacion: result.display_name.split(",").slice(0, 3).join(", "), // Tomar solo las primeras 3 partes
    }))

    setShowSearchResults(false)
    setSearchQuery("")
  }

  // Manejar búsqueda al presionar Enter
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      searchLocation(searchQuery)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Validar todos los campos antes de enviar
    const emailValid = validateField("email_contacto", form.email_contacto)
    const phoneValid = validateField("telefono_contacto", form.telefono_contacto)

    if (!emailValid || !phoneValid) {
      setIsSubmitting(false)
      showModal("error", "Datos Inválidos", "Por favor corrige los errores en el formulario antes de continuar.")
      return
    }

    // Mostrar modal de loading
    showModal("loading", "Enviando Encuesta", "Por favor espera mientras procesamos tu información...")

    try {
      // Formatear los datos usando el servicio
      const formattedData = encuestaService.formatEncuestaData(form)

      console.log("Datos a enviar:", formattedData) // Para debugging

      // Enviar datos usando el servicio
      const result = await encuestaService.saveEncuesta(formattedData)

      // Simular un pequeño delay para mostrar el loading
      setTimeout(() => {
        showModal("success", "¡Encuesta Enviada!", "Gracias por tu interés. Nos estaremos comunicando contigo.")
        resetForm()
      }, 2000) // 2 segundos de loading
    } catch (error) {
      console.error("Error al enviar encuesta:", error)
      showModal("error", "Error al Enviar", `No se pudo enviar la encuesta: ${error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Verificar si se puede avanzar al siguiente paso
  const canProceedToStep2 = () => {
    const hasRequiredFields = form.nombre_contacto && form.email_contacto && form.telefono_contacto
    const hasValidEmail = form.email_contacto && validateEmail(form.email_contacto)
    const hasValidPhone = form.telefono_contacto && validatePhone(form.telefono_contacto)

    return hasRequiredFields && hasValidEmail && hasValidPhone
  }

  const canProceedToStep3 = () => {
    return (
      form.nombre_parking && form.plazas && form.ubicacion && form.preciotarifa && form.horaApertura && form.horaCierre
    )
  }

  const canSubmit = () => {
    return form.latitud && form.longitud
  }

  return (
    <div className="encuesta-container">
      <header className="encuesta-header">
        <img src={logo || "/placeholder.svg"} alt="Parking Club" className="encuesta-logo" />
        <h1 className="encuesta-title">
          Parking <span className="title-club">Club</span>
        </h1>
      </header>

      <form className="encuesta-form" onSubmit={handleSubmit}>
        {/* Indicador de progreso */}
        <div className="progress-container">
          <div className="progress-steps">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`progress-step ${currentStep === step ? "active" : ""} ${currentStep > step ? "completed" : ""}`}
              >
                <div className="step-number">{step}</div>
                <div className="step-label">
                  {step === 1 && "Datos de contacto"}
                  {step === 2 && "Datos del parqueadero"}
                  {step === 3 && "Ubicación"}
                </div>
              </div>
            ))}
            <div className="progress-line">
              <div
                className="progress-line-inner"
                style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        <FormStep isActive={currentStep === 1}>
          <h2 className="step-title">Datos de contacto</h2>
          <p className="step-description">Ingresa tus datos para que podamos contactarte</p>

          <div className="form-grid">
            <div className="form-column">
              {/* Nombre del propietario */}
              <div className="form-group">
                <label htmlFor="nombre_contacto">
                  <User size={18} /> Nombre completo
                </label>
                <input
                  type="text"
                  id="nombre_contacto"
                  name="nombre_contacto"
                  value={form.nombre_contacto}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  placeholder="Ingresa tu nombre completo"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email_contacto">
                  <Mail size={18} /> Correo electrónico
                </label>
                <input
                  type="email"
                  id="email_contacto"
                  name="email_contacto"
                  value={form.email_contacto}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  placeholder="ejemplo@correo.com"
                />
                {validationErrors.email_contacto && (
                  <div className="validation-error">{validationErrors.email_contacto}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="telefono_contacto">
                  <Phone size={18} /> Teléfono de contacto
                </label>
                <input
                  type="tel"
                  id="telefono_contacto"
                  name="telefono_contacto"
                  value={form.telefono_contacto}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  placeholder="0999999999"
                />
                {validationErrors.telefono_contacto && (
                  <div className="validation-error">{validationErrors.telefono_contacto}</div>
                )}
              </div>
            </div>

            <div className="form-column contact-info">
              <div className="contact-info-content">
                <h3>¿Por qué necesitamos tus datos?</h3>
                <p>
                  Tus datos de contacto nos permiten comunicarnos contigo para brindarte información sobre los
                  beneficios de unirte a Parking Club.
                </p>
                <ul>
                  <li>Mayor visibilidad para tu parqueadero</li>
                  <li>Incremento en tus ingresos</li>
                  <li>Reportes y estadísticas</li>
                </ul>
                <p>
                  <strong>Tu información está segura con nosotros.</strong> No compartimos tus datos con terceros.
                </p>
              </div>
            </div>
          </div>

          <div className="form-navigation">
            <div></div> {/* Espacio vacío para alinear */}
            <button type="button" className="btn-next" onClick={nextStep} disabled={!canProceedToStep2()}>
              Siguiente
            </button>
          </div>
        </FormStep>

        <FormStep isActive={currentStep === 2}>
          <h2 className="step-title">Datos del parqueadero</h2>
          <p className="step-description">Información básica sobre tu establecimiento</p>

          <div className="form-grid">
            {/* Columna Izquierda */}
            <div className="form-column">
              {/* Nombre */}
              <div className="form-group">
                <label htmlFor="nombre_parking">
                  <Home size={18} /> Nombre del Parking
                </label>
                <input
                  type="text"
                  id="nombre_parking"
                  name="nombre_parking"
                  value={form.nombre_parking}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  placeholder="Nombre de tu parqueadero"
                />
              </div>

              <div className="form-group">
                <label htmlFor="ubicacion">
                  <MapPin size={18} /> Dirección
                </label>
                <input
                  type="text"
                  id="ubicacion"
                  name="ubicacion"
                  value={form.ubicacion}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  placeholder="Dirección de tu parqueadero"
                />
              </div>

              {/* Plazas */}
              <div className="form-group">
                <label htmlFor="plazas">
                  <Grid size={18} /> Plazas Disponibles
                </label>
                <input
                  type="number"
                  id="plazas"
                  name="plazas"
                  value={form.plazas}
                  onChange={handleChange}
                  required
                  min="0"
                  disabled={isSubmitting}
                  placeholder="Cantidad de espacios"
                />
              </div>
            </div>

            <div className="form-column">
              {/* Tarifa */}
              <div className="form-group">
                <label htmlFor="preciotarifa">
                  <DollarSign size={18} /> Tarifa por hora ($)
                </label>
                <input
                  type="number"
                  id="preciotarifa"
                  name="preciotarifa"
                  value={form.preciotarifa}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  disabled={isSubmitting}
                  placeholder="0.00"
                />
              </div>

              {/* Horarios */}
              <div className="form-row">
                <div className="form-group half">
                  <label htmlFor="horaApertura">
                    <Clock size={18} /> Hora de Apertura
                  </label>
                  <input
                    type="time"
                    id="horaApertura"
                    name="horaApertura"
                    value={form.horaApertura}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group half">
                  <label htmlFor="horaCierre">
                    <Clock size={18} /> Hora de Cierre
                  </label>
                  <input
                    type="time"
                    id="horaCierre"
                    name="horaCierre"
                    value={form.horaCierre}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Descripción */}
              <div className="form-group">
                <label htmlFor="descripcion">
                  <AlignLeft size={18} /> Descripción Adicional
                </label>
                <textarea
                  id="descripcion"
                  name="descripcion"
                  value={form.descripcion}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Describe características especiales, servicios adicionales, etc."
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          <div className="form-navigation">
            <button type="button" className="btn-back" onClick={prevStep}>
              Atrás
            </button>
            <button type="button" className="btn-next" onClick={nextStep} disabled={!canProceedToStep3()}>
              Siguiente
            </button>
          </div>
        </FormStep>

        <FormStep isActive={currentStep === 3}>
          <h2 className="step-title">Ubicación en el mapa</h2>
          <p className="step-description">Selecciona la ubicación exacta de tu parqueadero</p>

          <div className="form-grid">
            <div className="form-column full-width">
              {/* Controles de ubicación */}
              <div className="location-controls">
                <button
                  type="button"
                  className={`btn-location ${isGettingLocation ? "loading" : ""}`}
                  onClick={getCurrentLocation}
                  disabled={isSubmitting || isGettingLocation}
                >
                  <Locate size={16} />
                  {isGettingLocation ? "Obteniendo..." : "Mi Ubicación"}
                </button>

                <div className="search-container">
                  <div className="search-input-wrapper">
                    <Search size={16} className="search-icon" />
                    <input
                      type="text"
                      placeholder="Buscar dirección..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                      disabled={isSubmitting}
                      className="search-input"
                    />
                    <button
                      type="button"
                      className={`btn-search ${isSearching ? "loading" : ""}`}
                      onClick={() => searchLocation(searchQuery)}
                      disabled={isSubmitting || isSearching || !searchQuery.trim()}
                      aria-label="Buscar dirección"
                    >
                      {isSearching ? <div className="spinner"></div> : <Search size={18} />}
                    </button>
                  </div>

                  {/* Resultados de búsqueda */}
                  {showSearchResults && (
                    <div className="search-results">
                      {searchResults.map((result, index) => (
                        <div key={index} className="search-result-item" onClick={() => selectSearchResult(result)}>
                          <MapPin size={14} />
                          <div className="result-info">
                            <div className="result-name">{result.display_name.split(",")[0]}</div>
                            <div className="result-address">{result.display_name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="map-wrapper">
                <MapContainer
                  center={defaultCenter}
                  zoom={13}
                  scrollWheelZoom={false}
                  style={{ height: "300px", width: "100%" }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapCenterController
                    center={form.latitud && form.longitud ? [form.latitud, form.longitud] : defaultCenter}
                    zoom={15}
                  />
                  <LocationPicker onSelect={handleMapSelect} />
                  {form.latitud && form.longitud && (
                    <Marker position={[form.latitud, form.longitud]} icon={markerIcon} />
                  )}
                </MapContainer>
              </div>

              <div className="coords-display">
                {form.latitud && form.longitud
                  ? `Coordenadas: ${form.latitud.toFixed(5)}, ${form.longitud.toFixed(5)}`
                  : "Usa 'Mi Ubicación', busca una dirección, o haz clic en el mapa"}
              </div>
            </div>
          </div>

          <div className="form-navigation">
            <button type="button" className="btn-back" onClick={prevStep}>
              Atrás
            </button>
            <button
              type="submit"
              className={`btn-submit-step ${isSubmitting ? "loading" : ""}`}
              disabled={isSubmitting || !canSubmit()}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Tag size={18} style={{ marginRight: "0.5rem" }} />
                  Enviar Encuesta
                </>
              )}
            </button>
          </div>
        </FormStep>
      </form>

      {/* Modal moderno */}
      <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} />
    </div>
  )
}
